import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Input, Label, Button } from "reactstrap";
import { toast } from "react-toastify";
import { createKeySummary } from "slices/Course/thunk";
import { AppDispatch } from "store";

interface SummaryData {
  headline: string;
  summaryText: string;
  iconFile?: File | null;
  iconPreview?: string;
  isSaved: boolean;
}

interface KeySummaryFormProps {
  validation: any; 
  isEdit?: boolean;
}

const KeySummaryForm: React.FC<KeySummaryFormProps> = ({
  validation,
  isEdit = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [iconIndex, setIconIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);

  const toBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const dispatch = useDispatch<AppDispatch>();

  const summaries =
    validation.values.summaries?.length > 0
      ? validation.values.summaries
      : [
          {
            headline: "",
            summaryText: "",
            iconFile: null,
            iconPreview: "",
            isSaved: false,
          },
        ];

  const addSummary = () => {
    const newSummary: SummaryData = {
      headline: "",
      summaryText: "",
      iconFile: null,
      iconPreview: "",
      isSaved: false,
    };

    const updated = [...summaries, newSummary];
    validation.setFieldValue("summaries", updated);
  };

  const openFilePicker = (index: number) => {
    setIconIndex(index);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || iconIndex === null) return;

    const file = e.target.files[0];
    if (!file) return;

    const base64 = await toBase64(file);

    const updated = [...summaries];
    updated[iconIndex].iconFile = file;
    updated[iconIndex].iconPreview = base64;  // Here we store base64

    validation.setFieldValue("summaries", updated);
  };

  const saveSummaries = async () => {
    if (!validation.values.course_id) {
      toast.error("Please save Basic Info first so we have a course id.");
      return;
    }

    try {
      setSaving(true);
      for (let s of summaries) {
        const payload = {
          course_id: validation.values.course_id,
          head_line: s.headline,
          keysummary: s.summaryText,
          image_path: s.iconPreview || "", // base64 string
        };

        await dispatch(createKeySummary(payload));
      }

      const markedSaved = summaries.map((item: any) => ({
        ...item,
        isSaved: true,
      }));

      validation.setFieldValue("summaries", markedSaved);
      setHasSavedOnce(true);
      toast.success("Key summary saved");
    } catch (error) {
      toast.error("Failed to save key summary. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const showAddButton =
    hasSavedOnce ||
    summaries.some((summary: SummaryData) => summary.isSaved);

  const disableSave =
    summaries.some((s: { headline: any; summaryText: any; }) => !s.headline || !s.summaryText) || summaries.length === 0;

  return (
    <form onSubmit={(e) => e.preventDefault()}>

      {showAddButton && (
        <div className="d-flex justify-content-end mb-3">
          <Button color="success" onClick={addSummary}>
            <i className="ri-add-line me-1" /> Add Key Summary
          </Button>
        </div>
      )}

      {/* SUMMARY CARDS */}
      {summaries.map((item: SummaryData, index: number) => (
        <div
          key={index}
          className="border rounded p-3 mb-4 shadow-sm"
        >
          <div className="d-flex justify-content-between mb-3">
            <h6 className="fw-bold">Summary {index + 1}</h6>

            <div
              className="d-flex align-items-center px-2 py-1 border rounded"
              style={{ cursor: "pointer" }}
              onClick={() => openFilePicker(index)}
            >
              <span className="me-2 text-muted">Add Icon</span>
              <i className="ri-add-line text-success" />
            </div>
          </div>

          {/* ICON */}
          {item.iconPreview && (
            <img
              src={item.iconPreview}
              height={50}
              className="rounded mb-3"
              alt="summary-icon"
            />
          )}

          <Row className="g-3">
            <Col md={12}>
              <Label>Headline *</Label>
              <Input
                type="text"
                placeholder="Headline"
                disabled={item.isSaved}
                value={item.headline}
                onChange={(e) => {
                  const updated = [...summaries];
                  updated[index].headline = e.target.value;
                  validation.setFieldValue("summaries", updated);
                }}
              />
            </Col>

            <Col md={12}>
              <Label>Summary *</Label>
              <Input
                type="textarea"
                placeholder="Summary"
                disabled={item.isSaved}
                rows={3}
                value={item.summaryText}
                onChange={(e) => {
                  const updated = [...summaries];
                  updated[index].summaryText = e.target.value;
                  validation.setFieldValue("summaries", updated);
                }}
              />
            </Col>
          </Row>
        </div>
      ))}

      {/* HIDDEN FILE PICKER */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/png,image/jpeg"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* SAVE BUTTON */}
      <div className="d-flex justify-content-end mt-4">
        <Button
          color="primary"
          onClick={saveSummaries}
          disabled={disableSave || saving}
        >
          {saving ? <i className="fa fa-spinner fa-spin me-2" /> : null}
          {isEdit ? "Update Summary" : "Save"}
        </Button>
      </div>
    </form>
  );
};

export default KeySummaryForm;
